import { Link } from 'react-router';
import type { SessionTrackItem } from '@lp/shared';
import { CheckCircle2, ChevronRight, Circle, Clock, Lock, PlayCircle, Trophy } from 'lucide-react';
import { Badge, Card, DotProgress, ProgressBar, clsx } from '../ui.js';
import { ComponentBadges } from './ComponentBadges.js';
import { formatMinutes, pct } from '../../lib/format.js';
import type { CoursePageCapstone } from '../../lib/contracts.js';

/**
 * The course track.
 *
 * A locked session is rendered, in place, with the server's own reason
 * attached — never hidden and never clickable. Status is carried by an icon, a
 * word and a progress bar together, so it survives being read without colour.
 */
export function SessionTrack({
  courseSlug,
  sessions,
  capstone,
}: {
  courseSlug: string;
  sessions: SessionTrackItem[];
  capstone: CoursePageCapstone | null;
}) {
  return (
    <ol className="flex flex-col gap-3" aria-label="Sessions in this course">
      {sessions.map((session) => (
        <SessionRow key={session.id} courseSlug={courseSlug} session={session} />
      ))}
      {capstone ? (
        <li>
          <CapstoneTrackCard courseSlug={courseSlug} capstone={capstone} />
        </li>
      ) : null}
    </ol>
  );
}

function statusOf(session: SessionTrackItem): {
  label: string;
  icon: React.ReactNode;
  tone: 'ok' | 'brand' | 'neutral';
} {
  if (session.progress.completed) {
    return { label: 'Completed', icon: <CheckCircle2 size={16} aria-hidden />, tone: 'ok' };
  }
  if (session.progress.watchPct > 0) {
    return {
      label: `In progress · ${pct(session.progress.watchPct)} watched`,
      icon: <PlayCircle size={16} aria-hidden />,
      tone: 'brand',
    };
  }
  return { label: 'Not started', icon: <Circle size={16} aria-hidden />, tone: 'neutral' };
}

function SessionRow({
  courseSlug,
  session,
}: {
  courseSlug: string;
  session: SessionTrackItem;
}) {
  const locked = !session.unlocked;
  const isDraft = session.status !== 'published';
  const status = statusOf(session);
  const href = `/courses/${courseSlug}/sessions/${session.slug}`;

  const body = (
    <div className="flex items-start gap-4 p-4 sm:p-5">
      <span
        aria-hidden
        className={clsx(
          'grid place-items-center h-9 w-9 shrink-0 rounded-full border text-sm font-semibold tabular-nums',
          locked
            ? 'border-line bg-surface-2 text-ink-subtle'
            : session.progress.completed
              ? 'border-accent/40 bg-accent-wash text-ok'
              : 'border-line-strong bg-surface-2 text-ink',
        )}
      >
        {locked ? <Lock size={15} /> : session.orderIndex + 1}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="font-semibold text-ink leading-tight">
            <span className="sr-only">Session {session.orderIndex + 1}: </span>
            {session.title}
          </h3>
          {isDraft ? <Badge tone="warn">Draft — not visible to learners</Badge> : null}
        </div>

        {session.summary ? (
          <p className="text-sm text-ink-muted mt-1 leading-relaxed line-clamp-2">
            {session.summary}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2.5">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 text-xs font-medium',
              status.tone === 'ok'
                ? 'text-ok'
                : status.tone === 'brand'
                  ? 'text-brand-text'
                  : 'text-ink-subtle',
            )}
          >
            {status.icon}
            {status.label}
          </span>
          {session.estimatedDurationMin ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
              <Clock size={13} aria-hidden />
              {formatMinutes(session.estimatedDurationMin)}
            </span>
          ) : null}
          {session.quizBest ? (
            <Badge tone={session.quizBest.passed ? 'ok' : 'neutral'}>
              Best quiz {pct(session.quizBest.scorePct)}
              <span className="sr-only">
                {' '}
                across {session.quizBest.attempts} attempt
                {session.quizBest.attempts === 1 ? '' : 's'}
              </span>
            </Badge>
          ) : null}
        </div>

        <div className="mt-3">
          <ComponentBadges components={session.components} />
        </div>

        {session.progress.watchPct > 0 && !session.progress.completed ? (
          <ProgressBar
            className="mt-3"
            value={session.progress.watchPct}
            label={`${session.title} watch progress`}
          />
        ) : null}

        {locked && session.lockedReason ? (
          <p className="mt-3 flex items-start gap-2 text-xs text-ink-muted bg-warn-wash border border-warn/30 rounded-control px-3 py-2">
            <Lock size={13} className="mt-0.5 shrink-0" aria-hidden />
            <span>
              <span className="font-medium">Locked.</span> {session.lockedReason}
            </span>
          </p>
        ) : null}
      </div>

      {!locked ? (
        <ChevronRight size={18} className="text-ink-subtle shrink-0 mt-2" aria-hidden />
      ) : null}
    </div>
  );

  if (locked) {
    return (
      <Card as="li" className="bg-surface-2/60">
        <div aria-disabled="true">{body}</div>
      </Card>
    );
  }

  return (
    <Card as="li" className="hover:border-line-strong transition-colors">
      <Link to={href} className="block rounded-card">
        {body}
      </Link>
    </Card>
  );
}

/**
 * The capstone is not one more session. It is pinned to the end of the track
 * and built to read as the thing the course was leading to — its own shape,
 * its own wash, its own "ungraded" promise up front.
 */
function CapstoneTrackCard({
  courseSlug,
  capstone,
}: {
  courseSlug: string;
  capstone: CoursePageCapstone;
}) {
  const href = `/courses/${courseSlug}/capstone`;
  const locked = capstone.locked;

  const inner = (
    <div className="p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className="grid place-items-center h-11 w-11 shrink-0 rounded-card bg-surface text-project border border-project/30"
        >
          {locked ? <Lock size={19} /> : <Trophy size={19} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="eyebrow text-project">Capstone project</p>
          <h3 className="text-lg font-semibold tracking-tight text-ink mt-1 leading-tight">
            {capstone.title}
          </h3>
          {capstone.subtitle ? (
            <p className="text-sm text-ink-muted mt-1 leading-relaxed">{capstone.subtitle}</p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Badge tone="project">Ungraded — build it for yourself</Badge>
            {capstone.effortEstimate ? <Badge>{capstone.effortEstimate}</Badge> : null}
            {capstone.status !== 'published' ? <Badge tone="warn">Draft</Badge> : null}
          </div>

          {capstone.milestoneCount > 0 ? (
            <div className="mt-3">
              <span className="text-xs text-ink-muted mr-2">Milestones</span>
              <DotProgress done={capstone.milestonesDone} total={capstone.milestoneCount} />
            </div>
          ) : null}

          {locked && capstone.lockedReason ? (
            <p className="mt-4 flex items-start gap-2 text-xs text-ink-muted bg-warn-wash border border-warn/30 rounded-control px-3 py-2">
              <Lock size={13} className="mt-0.5 shrink-0" aria-hidden />
              <span>
                <span className="font-medium">Locked.</span> {capstone.lockedReason}
              </span>
            </p>
          ) : (
            <p className="mt-4 text-sm font-medium text-brand-text inline-flex items-center gap-1.5">
              Open the brief
              <ChevronRight size={15} aria-hidden />
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (locked) {
    return (
      <Card className="border-project/25 bg-project-wash/60">
        <div aria-disabled="true">{inner}</div>
      </Card>
    );
  }

  return (
    <Card className="border-project/25 bg-project-wash hover:border-project/50 transition-colors duration-150 ease-out">
      <Link to={href} className="block rounded-card">
        {inner}
      </Link>
    </Card>
  );
}
